import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Spin, Modal, Row, Col } from "antd";
import DetailTabs from "@components/DetailTabs";
import { api } from "@redux/api";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";

import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import Workorder from "@containers/facility/Workorder/Workorder";
import _ from "lodash";
import "./index.css";
import { buildFacilityPath, getFacilityContext, getLeafFromPathname } from "@/utils/route-context";
import Select from "@components/ResponsiveSelect";

const { TabPane } = DetailTabs;
const { TextArea } = Input;
const { Option } = Select;

const ProjectDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const projectListPath = buildFacilityPath("project", facilityContext) || "/switch-view";

  // Determine if this is a create page from either canonical or legacy route
  const isCreatePage = isAdd || getLeafFromPathname(location.pathname, "facility") === "create-project";

  const [loading, setLoading] = useState(isCreatePage ? false : true);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [projectData, setProjectData] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1");

  const userName = useSelector(state => 
    state?.Auth?.user || 
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  useEffect(() => {
    if (!isCreatePage && id) {
      fetchProjectDetail();
    }
  }, [id, isCreatePage]);

  const fetchProjectDetail = async () => {
    try {
      setLoading(true);
      const response = await api.filterProject(`projectId=${id}`);

      if (response.status === 200 && response.data.results.length > 0) {
        const data = response.data.results[0];
        setProjectData(data);

        // Set form values with date conversion
        const formValues = {
          ...data,
          created_date: data.created_date ? moment(data.created_date).format("YYYY-MM-DD HH:mm") : "",
          modified_date: data.modified_date ? moment(data.modified_date).format("YYYY-MM-DD HH:mm") : "",
        };
        form.setFieldsValue(formValues);
      } else {
        message.error("Project not found");
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      message.error("Failed to load project details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setReadOnly(false);
  };

  const handleCancel = () => {
    setReadOnly(true);
    if (projectData) {
      const formValues = {
        ...projectData,
        created_date: projectData.created_date ? moment(projectData.created_date).format("YYYY-MM-DD HH:mm") : "",
        modified_date: projectData.modified_date ? moment(projectData.modified_date).format("YYYY-MM-DD HH:mm") : "",
      };
      form.setFieldsValue(formValues);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Clean up invalid date values before submitting
      const submitValues = {};
      for (let key in values) {
        if (values[key] !== 'Invalid date' && values[key] !== null && values[key] !== undefined) {
          submitValues[key] = values[key];
        }
      }

      let response;
      if (isCreatePage) {
        // Create mode
        response = await api.createProject(submitValues);
      } else {
        // Edit mode
        response = await api.editProject(id, submitValues);
      }

      if (response.status === 200 || response.status === 201) {
        globalMessage.success({
          message: "Success",
          description: isCreatePage ? "Project created successfully" : "Project updated successfully",
        });

        if (isCreatePage) {
          // Refresh Project list page and close current tab
          refreshTab(projectListPath);
          closeTab();
          navigate(projectListPath);
        } else {
          setReadOnly(true);
          fetchProjectDetail();
        }
      } else {
        globalMessage.error({
          message: "Error",
          description: isCreatePage ? "Failed to create project" : "Failed to update project",
        });
      }
    } catch (error) {
      console.error(`Error ${isCreatePage ? 'creating' : 'updating'} project:`, error);
      globalMessage.error({
        message: "Error",
        description: isCreatePage ? "Failed to create project" : "Failed to update project",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this project?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteProject(id);

          if (response.status === 204 || response.status === 200) {
            globalMessage.success({
              message: "Success",
              description: "Project deleted successfully",
            });
            // Refresh Project list page and close current tab
            refreshTab(projectListPath);
            closeTab();
            navigate(projectListPath);
          } else {
            globalMessage.error({
              message: "Error",
              description: "Failed to delete project",
            });
          }
        } catch (error) {
          console.error("Error deleting project:", error);
          globalMessage.error({
            message: "Error",
            description: "Failed to delete project",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCancelCreate = () => {
    closeTab();
    navigate(projectListPath);
  };

  const renderOperationButton = () => {
    if (isCreatePage) {
      // Create mode - only show Add button
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={handleCancelCreate}>
            <i className="fa-solid fa-xmark pr-2"></i>Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
        </div>
      );
    }

    if (readOnly) {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="primary" onClick={handleEdit}>
            <i className="fa-solid fa-pen-to-square pr-2"></i>Edit
          </Button>
          <Button danger onClick={handleDelete}>
            <i className="fa-solid fa-trash pr-2"></i>Delete
          </Button>
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      );
    }
  };

  // Related component
  const ProjectRelatedComponent = () => {
    return <>{id ? <Workorder relatedList={true} parentProject={id} relatedObject={'project'} /> : ''}</>
  };

  if (loading && !projectData && !isCreatePage) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
        </div>
    );
}

  return (
    <div className="project-detail-container">
      <Spin spinning={loading}>
        <DetailTabs
          defaultActiveKey="1" 
          destroyInactiveTabPane={true}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
        >
          <TabPane
            tab={
              <span>
                <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
              </span>
            }
            key="1"
          >
            <div>
              <div className="slds-m-around_x-small">
                <div className="px-4">
                  <div className="w-full">
                    <Form
                      form={form}
                      onFinish={handleSubmit}
                      layout="horizontal"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      className="detailForm"
                        autoComplete="off">
                      <Row gutter={[0, 0]}>
                        {/* ID - Hidden in create mode */}
                        {!isCreatePage && (
                          <>
                            <Col xs={24} lg={12}>
                              <Form.Item label="ID" name="id">
                                <Input disabled />
                              </Form.Item>
                            </Col>
                            <Col xs={0} lg={12} />
                          </>
                        )}

                        {/* Name */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Name" 
                            name="name"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Status */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Status" name="status">
                            <Select disabled={readOnly}>
                              <Option value="Open">Open</Option>
                              <Option value="New">New</Option>
                              <Option value="Pending">Pending</Option>
                              <Option value="Completed">Completed</Option>
                              <Option value="Closed">Closed</Option>
                            </Select>
                          </Form.Item>
                        </Col>

                        {/* Priority */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Priority" name="priority">
                            <Select disabled={readOnly}>
                              <Option value="High">High</Option>
                              <Option value="Medium">Medium</Option>
                              <Option value="Low">Low</Option>
                            </Select>
                          </Form.Item>
                        </Col>

                        {/* External URL */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="External URL" name="externalUrl">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Description - Full Width */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Description" 
                            name="description" 
                          >
                            <TextArea rows={3} disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Comment - Full Width */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Comment" 
                            name="comment" 
                          >
                            <TextArea rows={3} disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Created By - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Created By" name="createdby_name">
                              <span>{projectData?.createdby_name}</span>
                            </Form.Item>
                          </Col>
                        )}

                        {/* Created Date - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Created Date" name="created_date">
                              <span>{projectData?.created_date ? moment(projectData.created_date).format("YYYY-MM-DD HH:mm") : ""}</span>
                            </Form.Item>
                          </Col>
                        )}

                        {/* Modified By - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Modified By" name="lastModified_name">
                              <span>{projectData?.lastModified_name}</span>
                            </Form.Item>
                          </Col>
                        )}

                        {/* Modified Date - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Modified Date" name="modified_date">
                              <span>{projectData?.modified_date ? moment(projectData.modified_date).format("YYYY-MM-DD HH:mm") : ""}</span>
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Related and Log tabs - Hidden in create mode */}
          {!isCreatePage && (
            <>
              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-table-list pr-2"></i>Related
                  </span>
                }
                key="2"
              >
                <div>
                  <ProjectRelatedComponent />
                </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-timeline pr-2"></i>Log
                  </span>
                }
                key="3"
              >
                <div className="px-4">
                  <RecordAuditTimeline moduleId={id} module="Project" />
                </div>
              </TabPane>
            </>
          )}
        </DetailTabs>
      </Spin>
    </div>
  );
};

export default ProjectDetail;
